import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { BrandMark } from '@/components/brand/BrandMark';
import { SESSION_EXPIRED_KEY, useAuth } from '@/lib/auth';
import { env } from '@/lib/env';
import { errorMessage } from '@/lib/api';
import { Button, ErrorBox, Field, Input } from '@/components/ui';
import { ForgotPassword } from './password';
import { MfaChallenge } from './mfa';
import { padUntil, signInErrorMessage } from './signInError';
import { FRIENDLY } from '@tradeoutlet/shared';
import { rememberNext, safeNext } from '../../lib/signInNext';
import { useBrand } from '@/lib/branding';

const DEMO_USERS = [
  { email: 'daniel@tradeoutlet.test', name: 'Daniel McAllister', role: 'Owner / admin' },
  { email: 'sarah@tradeoutlet.test', name: 'Sarah Patel', role: 'Ops manager' },
  { email: 'james@tradeoutlet.test', name: 'James Wilson', role: 'Warehouse supervisor' },
  { email: 'priya@tradeoutlet.test', name: 'Priya Shah', role: 'Store supervisor' },
  { email: 'tom@tradeoutlet.test', name: 'Tom Evans', role: 'Picker (17, young worker)' },
  { email: 'emma@tradeoutlet.test', name: 'Emma Clarke', role: 'Sales (zero hours)' },
];

export function SignInPage() {
  const auth = useAuth();
  const brand = useBrand();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(() => (sessionStorage.getItem(SESSION_EXPIRED_KEY) ? FRIENDLY.expired : null));
  useEffect(() => { sessionStorage.removeItem(SESSION_EXPIRED_KEY); }, []);
  const [busy, setBusy] = useState(false);
  const next = safeNext(params.get('next'));
  const [forgot, setForgot] = useState(params.get('forgot') === '1');

  async function run(fn: () => Promise<void>, message: (e: unknown) => string = errorMessage) {
    setBusy(true);
    setError(null);
    try { await fn(); nav(next, { replace: true }); } catch (e) { setError(message(e)); } finally { setBusy(false); }
  }

  // t402u: one message for every account problem (see signInError.ts).
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const started = Date.now();
    void run(async () => { try { await auth.signInWithPassword(email, password); } catch (err) { await padUntil(started); throw err; } },
      err => signInErrorMessage(err, FRIENDLY.offline));
  };

  return (
    <div className="on-dark relative flex min-h-full items-center justify-center overflow-hidden bg-ink p-6">
      {/* Brand band across the top, and a soft yellow glow behind the card */}
      <div className="absolute inset-x-0 top-0 h-2 bg-brand-yellow" aria-hidden />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[36rem] w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-yellow/10 blur-3xl" aria-hidden />
      <div className="relative w-full max-w-md">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          {/* t401u: The Hub's own sign-in is neutral (lockup only). A company's own address (<sub>.thehubapp.co.uk, its
              domain) may show its name before sign-in; app.thehubapp.co.uk never does (API: isPlatformHost). */}
          <BrandMark height={44} />
          {brand.custom && <div className="text-lg font-bold text-white" data-testid="app-name">{brand.title}</div>}
        </div>
        <div className="on-page rounded-2xl bg-white p-7 shadow-2xl ring-1 ring-white/10 dark:bg-slate-900">
          {auth.status === 'mfa' ? (
            <MfaChallenge onDone={() => nav(next, { replace: true })} />
          ) : auth.mode === 'supabase' && forgot ? (
            <ForgotPassword initialEmail={email} onBack={() => setForgot(false)} />
          ) : auth.mode === 'supabase' ? (
            <>
              <h1 className="mb-5 text-lg font-bold">Sign in<span className="mt-1.5 block h-1 w-10 rounded-full bg-accent-line" aria-hidden /></h1>
              {env.entraSso && (
                <>
                  <Button variant="secondary" className="w-full" onClick={() => void run(async () => { rememberNext(next); await auth.signInWithMicrosoft(); })} disabled={busy}>
                    <svg viewBox="0 0 21 21" className="h-4 w-4"><path fill="#f25022" d="M1 1h9v9H1z" /><path fill="#7fba00" d="M11 1h9v9h-9z" /><path fill="#00a4ef" d="M1 11h9v9H1z" /><path fill="#ffb900" d="M11 11h9v9h-9z" /></svg>
                    Sign in with Microsoft
                  </Button>
                  <div className="my-5 flex items-center gap-3 text-xs text-slate-500"><div className="h-px flex-1 bg-slate-200" />or<div className="h-px flex-1 bg-slate-200" /></div>
                </>
              )}
              <form onSubmit={submit} className="space-y-4">
                <Field label="Email address"><Input type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} /></Field>
                <Field label="Password"><Input type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} /></Field>
                <div className="-mt-2 text-right"><button type="button" className="text-sm font-medium text-slate-600 underline-offset-2 hover:underline dark:text-slate-300" onClick={() => setForgot(true)} data-testid="forgot-link">Forgot password?</button></div>
                <ErrorBox error={error} />
                <Button type="submit" className="w-full" loading={busy}>Sign in</Button>
              </form>
            </>
          ) : (
            <>
              <h1 className="text-lg font-bold">Local development sign-in<span className="mt-1.5 block h-1 w-10 rounded-full bg-accent-line" aria-hidden /></h1>
              <p className="mb-4 mt-1 text-sm text-slate-500">Supabase isn't configured (VITE_SUPABASE_URL), so pick a seeded demo user. The API must run with <code className="rounded bg-slate-100 px-1 text-xs dark:bg-slate-800">DEV_AUTH_ENABLED=true</code>.</p>
              <div className="space-y-2">
                {DEMO_USERS.map(u => (
                  <button key={u.email} disabled={busy} onClick={() => void run(() => auth.devSignIn(u.email))}
                    className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-4 py-2.5 text-left transition hover:border-accent-line hover:bg-accent-soft dark:border-slate-700 dark:hover:bg-slate-800">
                    <span><span className="block text-sm font-medium">{u.name}</span><span className="block text-xs text-slate-500">{u.role}</span></span>
                    <span className="text-xs text-slate-500">{u.email}</span>
                  </button>
                ))}
              </div>
              <ErrorBox error={error} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
